<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px;">
      <div class="card card-sm">
        <div class="card-header" style="background-color: #DBECDB;">
          <h5 class="card-title">Gestión de Ventanilla</h5>
          <div class="row mb-3">
            <div class="col-md-8">
              <div class="form-group">
                <div class="input-group">
                  <input 
                    v-model="filters.search" 
                    type="text" 
                    class="form-control" 
                    placeholder="Buscar por detalle o área..."
                  >
                  <select
                    v-model="filters.estado"
                    class="form-select"
                    style="max-width: 150px;"
                  >
                    <option value="">Todos</option>
                    <option value="pendiente">Pendientes</option>
                    <option value="completado">Completados</option>
                    <option value="cancelado">Cancelados</option>
                  </select>
                  <select
                    v-model="filters.area"
                    class="form-select"
                    style="max-width: 200px;"
                  >
                    <option value="">Todas las áreas</option>
                    <option v-for="area in areas" :key="area.id" :value="area.id">
                      {{ area.nombre_area }}
                    </option>
                  </select>
                  <button 
                    class="btn btn-outline-secondary" 
                    type="button" 
                    @click="handleSearch"
                  >
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div class="col-md-4 text-end">
              <BButton v-if="hasPermission('almventanilla_create')"
                @click="showModal(null)"
                variant="primary"
                class="btn-round me-2"
              >
                <i class="fa fa-plus me-1"></i> Nuevo Registro
              </BButton>
              <BButton
                variant="danger"
                class="btn-round"
                @click="generatePDF"
              >
                <i class="fa fa-file-pdf me-1"></i> PDF
              </BButton>
            </div>
          </div>
        </div>
        
        <div class="card-body">
          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
          </div>
          
          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered">
                <thead class="text-primary">
                  <tr style="line-height: 8px; font-size: 0.8rem;">
                    <th>N°</th>
                    <th>Fecha</th>
                    <th>Detalle</th>
                    <th>Items</th>
                    <th>Estado</th>
                    <th>Registrado por</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ventanilla in ventanillas.data" :key="ventanilla.id">
                    <td>{{ ventanilla.numero }}</td>
                    <td>{{ formatDate(ventanilla.fecha_registro) }}</td>
                    <td>{{ ventanilla.detalle_entrada }}</td>
                    <td>{{ ventanilla.detalles.length }}</td>
                    <td>
                      <BBadge :variant="getBadgeVariant(ventanilla.estado)">
                        {{ ventanilla.estado }}
                      </BBadge>
                    </td>
                    <td>
                      <span v-if="ventanilla.usuario_registro">
                        {{ ventanilla.usuario_registro.nombres }} {{ ventanilla.usuario_registro.paterno }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <BButton
                          @click="showDetails(ventanilla.id)"
                          variant="info"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-eye"></i>
                        </BButton>
                        <BButton v-if="hasPermission('almventanilla_update')"
                          @click="showModal(ventanilla)"
                          variant="warning"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-edit"></i>
                        </BButton>
                        <BButton v-if="hasPermission('almventanilla_delete')"
                          @click="confirmDelete(ventanilla)"
                          variant="danger"
                          size="sm"
                        >
                          <i class="fa fa-trash"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="row mt-3" v-if="ventanillas.total > 0">
              <div class="col-md-12">
                <BPagination
                  v-model="pagination.current_page"
                  :total-rows="ventanillas.total"
                  :per-page="pagination.per_page"
                  @input="handlePageChange"
                  align="center"
                ></BPagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar -->
    <BModal
          v-model="modalVisible"
          :title="form.id ? 'Editar Registro' : 'Nuevo Registro'"
          size="xl"
          centered
          hide-footer
        >
          <form @submit.prevent="handleSubmit">
            <div class="form-group">
              <label class="form-label">Detalle *</label>
              <textarea
                v-model="form.detalle_entrada"
                class="form-control"
                :class="{ 'is-invalid': errors.detalle_entrada }"
                rows="3"
                required
                placeholder="Describa el detalle de la entrada"
              ></textarea>
              <div class="invalid-feedback" v-if="errors.detalle_entrada">
                {{ errors.detalle_entrada[0] }}
              </div>
            </div>

            <!-- Items -->
            <div class="form-group">
              <label class="form-label">Items *</label>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="bg-light">
                    <tr>
                      <th width="20%">Item</th>
                      <th width="80">Cantidad</th>
                      <th width="100">Serie</th>
                      <th width="100">Origen</th>
                      <th width="100">De N°</th>
                      <th width="100">A N°</th>
                      <th width="120">Costo Unitario</th>
                      <th width="80">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in form.items" :key="index">
                      <td>
                        <select
                          v-model="item.id_item"
                          class="form-select form-select-sm"
                          :class="{ 'is-invalid': errors[`items.${index}.id_item`] }"
                          required
                          @change="onItemChange(index)"
                        >
                          <option value="">Seleccionar item</option>
                          <option v-for="itemOption in items" :key="itemOption.id" :value="itemOption.id">
                            {{ itemOption.nombre_item }} ({{ itemOption.unidad_medida }})
                          </option>
                        </select>
                        <div class="invalid-feedback" v-if="errors[`items.${index}.id_item`]">
                          {{ errors[`items.${index}.id_item`][0] }}
                        </div>
                      </td>
                      <td>
                        <input
                          v-model="item.cantidad"
                          type="number"
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': errors[`items.${index}.cantidad`] }"
                          min="1"
                          required
                          @change="onCantidadChange(index)"
                        >
                        <div class="invalid-feedback" v-if="errors[`items.${index}.cantidad`]">
                          {{ errors[`items.${index}.cantidad`][0] }}
                        </div>
                      </td>
                      <td>
                        <select
                          v-model="item.serie"
                          class="form-select form-select-sm"
                          :class="{ 'is-invalid': errors[`items.${index}.serie`] }"
                          required
                          @change="onSerieChange(index)"
                        >
                          <option value="">Serie</option>
                          <option v-for="letra in series" :key="letra" :value="letra">
                            {{ letra }}
                          </option>
                        </select>
                        <div class="invalid-feedback" v-if="errors[`items.${index}.serie`]">
                          {{ errors[`items.${index}.serie`][0] }}
                        </div>
                      </td>
                      <td>
                        <select
                          v-model="item.origen"
                          class="form-select form-select-sm"
                          :class="{ 'is-invalid': errors[`items.${index}.origen`] }"
                          required
                        >
                          <option value="">Origen</option>
                          <option value="DDE">DDE</option>
                          <option value="MINEDU">MINEDU</option>
                        </select>
                        <div class="invalid-feedback" v-if="errors[`items.${index}.origen`]">
                          {{ errors[`items.${index}.origen`][0] }}
                        </div>
                      </td>
                      <td>
                        <input
                          v-model="item.de_nro"
                          type="number"
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': errors[`items.${index}.de_nro`] }"
                          min="1"
                          required
                          @change="onNumeroChange(index)"
                        >
                        <div class="invalid-feedback" v-if="errors[`items.${index}.de_nro`]">
                          {{ errors[`items.${index}.de_nro`][0] }}
                        </div>
                      </td>
                      <td>
                        <input
                          v-model="item.a_nro"
                          type="number"
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': errors[`items.${index}.a_nro`] }"
                          min="1"
                          required
                          @change="onNumeroChange(index)"
                        >
                        <div class="invalid-feedback" v-if="errors[`items.${index}.a_nro`]">
                          {{ errors[`items.${index}.a_nro`][0] }}
                        </div>
                      </td>
                      <td>
                        <input
                          v-model="item.costo_unitario"
                          type="number"
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': errors[`items.${index}.costo_unitario`] }"
                          step="0.01"
                          min="0"
                          required
                        >
                        <div class="invalid-feedback" v-if="errors[`items.${index}.costo_unitario`]">
                          {{ errors[`items.${index}.costo_unitario`][0] }}
                        </div>
                      </td>
                      <td>
                        <BButton
                          @click="removeItem(index)"
                          variant="danger"
                          size="sm"
                          class="w-100"
                          :disabled="form.items.length === 1"
                        >
                          <i class="fa fa-trash"></i>
                        </BButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="mt-2">
                <small class="text-muted">
                  <i class="fa fa-info-circle"></i> 
                  El rango de números (De N° - A N°) debe coincidir con la cantidad. 
                  Ejemplo: Cantidad 5 = De 1 A 5
                </small>
              </div>
              <BButton
                @click="addItem"
                variant="outline-primary"
                size="sm"
                class="mt-2"
              >
                <i class="fa fa-plus me-1"></i> Agregar Item
              </BButton>
            </div>
            
            <div class="modal-footer mt-3">
              <BButton
                type="button"
                variant="secondary"
                @click="modalVisible = false"
                class="me-2"
              >
                Cancelar
              </BButton>
              <BButton
                type="submit"
                variant="primary"
                :disabled="loadingForm"
              >
                <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {{ form.id ? 'Actualizar' : 'Guardar' }}
              </BButton>
            </div>
          </form>
        </BModal>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BModal, BButton, BPagination, BBadge } from 'bootstrap-vue-3';
import { useAuthStore } from '../../stores/authStore';

const router = useRouter();

// Datos
const ventanillas = ref({ data: [] });
const areas = ref([]);
const items = ref([]);
const loading = ref(true);
const modalVisible = ref(false);
const loadingForm = ref(false);
const errors = ref({});
const series = ref('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''));

const authStore = useAuthStore();
// Filtros y paginación
const filters = ref({
  search: '',
  estado: '',
  area: ''
});

const pagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0
});

// Formulario
const form = ref({
  id: null,
  id_area: '',
  detalle_entrada: '',
  estado: 'pendiente',
   items: [{
    id_item: '',
    cantidad: 1,
    costo_unitario: 0,
    de_nro: 1,
    a_nro: 1,
    serie: '',
    origen: ''
  }]
});

const hasPermission = (permission) => {
    return authStore.hasPermission(permission);
};
// Métodos
const fetchVentanillas = async () => {
  loading.value = true;
  try {
    const params = {
      page: pagination.value.current_page,
      per_page: pagination.value.per_page,
      search: filters.value.search,
      estado: filters.value.estado,
      area: filters.value.area
    };

    const response = await axios.get('/ventanilla/listar', { params });
    ventanillas.value = response.data.data;
    pagination.value.total = response.data.data.total;
    console.log(ventanillas);
  } catch (error) {
    console.error('Error al cargar registros:', error);
    Swal.fire('Error', 'No se pudieron cargar los registros', 'error');
  } finally {
    loading.value = false;
  }
};

const fetchAreas = async () => {
  try {
    const response = await axios.get('/ventanilla/areas/listar');
    areas.value = response.data.data;
  } catch (error) {
    console.error('Error al cargar áreas:', error);
  }
};

const fetchItems = async () => {
  try {
    const response = await axios.get('/ventanilla/items/listar');
    items.value = response.data.data;
  } catch (error) {
    console.error('Error al cargar items:', error);
  }
};

const showModal = (ventanilla = null) => {
  if (ventanilla) {
    // Editar
    form.value = {
      id: ventanilla.id,
      id_area: ventanilla.id_area,
      detalle_entrada: ventanilla.detalle_entrada,
      estado: ventanilla.estado,
      items: ventanilla.detalles.map(detalle => ({
        id_item: detalle.id_item,
        cantidad: detalle.cantidad,
        costo_unitario: detalle.costo_unitario,
        de_nro: detalle.de_nro,
        a_nro: detalle.a_nro,
        serie: detalle.serie,
        origen: detalle.origen
      }))
    };
  } else {
    // Nuevo
    form.value = {
      id: null,
      id_area: '',
      detalle_entrada: '',
      estado: 'pendiente',
      items: [{
        id_item: '',
        cantidad: 1,
        costo_unitario: 0,
        de_nro: 1,
        a_nro: 1,
        serie: '',
        origen: ''
      }]
    };
  }
  errors.value = {};
  modalVisible.value = true;
};

const showDetails = (id) => {
  router.push({ name: 'ventanilla-detalle', params: { id } });
};

const addItem = () => {
  form.value.items.push({
    id_item: '',
    cantidad: 1,
    costo_unitario: 0,
    de_nro: 1,
    a_nro: 1,
    serie: '',
    origen: ''
  });
};

const removeItem = (index) => {
  if (form.value.items.length > 1) {
    form.value.items.splice(index, 1);
  }
};

const onItemChange = async (index) => {
  const item = form.value.items[index];
  if (item.id_item && item.serie && item.cantidad) {
    await calcularNumerosDisponibles(index);
  }
};

const onSerieChange = async (index) => {
  const item = form.value.items[index];
  if (item.id_item && item.serie && item.cantidad) {
    await calcularNumerosDisponibles(index);
  }
};

const onCantidadChange = async (index) => {
  const item = form.value.items[index];
  if (item.id_item && item.serie && item.cantidad) {
    await calcularNumerosDisponibles(index);
  } else {
    // Si no hay serie seleccionada, solo ajustar el número final
    item.a_nro = parseInt(item.de_nro) + parseInt(item.cantidad) - 1;
  }
};

const onNumeroChange = (index) => {
  const item = form.value.items[index];
  // Calcular cantidad basada en el rango de números
  const cantidadCalculada = (parseInt(item.a_nro) - parseInt(item.de_nro)) + 1;
  if (cantidadCalculada > 0) {
    item.cantidad = cantidadCalculada;
  }
  item.a_nro = parseInt(item.de_nro) + parseInt(item.cantidad) - 1;
};

const calcularNumerosDisponibles = async (index) => {
  const item = form.value.items[index];
  try {
    const response = await axios.get('/ventanilla/numeros-disponibles', {
      params: {
        id_item: item.id_item,
        serie: item.serie,
        cantidad: item.cantidad
      }
    });

    if (response.data.success) {
      item.de_nro = response.data.de_nro;
      item.a_nro = response.data.a_nro;
    }
  } catch (error) {
    console.error('Error al calcular números disponibles:', error);
    // En caso de error, calcular números consecutivos
    item.a_nro = parseInt(item.de_nro) + parseInt(item.cantidad) - 1;
  }
};

const handleSubmit = async () => {
  loadingForm.value = true;
  errors.value = {};

  try {
    const url = form.value.id 
      ? `/ventanilla/actualizar/${form.value.id}`
      : '/ventanilla/guardar';
    
    const method = form.value.id ? 'post' : 'post';

    const response = await axios[method](url, form.value);
    
    Swal.fire('Éxito', response.data.message, 'success');
    modalVisible.value = false;
    fetchVentanillas();
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors;
    } else {
      Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
    }
  } finally {
    loadingForm.value = false;
  }
};

const confirmDelete = (ventanilla) => {
  Swal.fire({
    title: '¿Estás seguro?',
    text: `Esta acción eliminará el registro #${ventanilla.numero}`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await axios.delete(`/ventanilla/${ventanilla.id}`);
        Swal.fire('Éxito', 'Registro eliminado correctamente', 'success');
        fetchVentanillas();
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Error al eliminar', 'error');
      }
    }
  });
};

const handleSearch = () => {
  pagination.value.current_page = 1;
  fetchVentanillas();
};

const handlePageChange = (page) => {
  pagination.value.current_page = page;
  fetchVentanillas();
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Date(dateString).toLocaleDateString('es-BO', options);
};

const getBadgeVariant = (estado) => {
  const variants = {
    pendiente: 'warning',
    completado: 'success',
    cancelado: 'danger'
  };
  return variants[estado] || 'secondary';
};

const generatePDF = async () => {
  try {
    const params = {
      estado: filters.value.estado,
      area: filters.value.area,
      search: filters.value.search
    };

    const response = await axios.get('/ventanilla/reporte/generar', {
      params: params,
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `reporte-ventanilla-${new Date().toISOString().split('T')[0]}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    Swal.fire('Error', 'No se pudo generar el PDF', 'error');
  }
};

// Inicialización
onMounted(() => {
  fetchVentanillas();
  fetchAreas();
  fetchItems();
});
</script>

<style scoped>
.card-header {
  background-color: #DBECDB;
}
.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}
thead th {
  background-color: #D84315;
  color:#ffffff;
  position: sticky;
  top: 0;
}
.btn-round {
  border-radius: 50px;
}
</style>